import React from 'react';
import styled from 'styled-components';
import get from 'lodash/get';
import find from 'lodash/find';
import capitalize from 'lodash/capitalize';

import { Box } from '@origin-digital/layout';
import { Text, LinkText, SegmentedControls, BREAK_POINTS } from '@origin-digital/style-guide';
import { getGreenData } from '@origin-digital/plans';
import { LaunchIcon } from '@origin-digital/icon';

import { IWizardStepProps, WizardContext } from '../../Wizard';
import { Container, Content, Actions, Heading, ContentText } from '../common';
import { Checkbox, CheckboxContainer } from '../common/Checkbox';
import { RADIO_TRUE, RADIO_FALSE } from '../constants';
import { YesIcon, NoIcon } from '../icons';

const SegmentedControlsContainer = styled(Box)`
  width: 100%;
  @media screen and (min-width: ${BREAK_POINTS.Min.MD}) {
    width: 548px;
  }
`;

const GreenOptionSelectors = styled.div`
  & > *:not(:last-child) {
    margin-bottom: 24px;
  }
`;

const GREEN_ZERO_VALUE = '0.00';

export class Renewables extends React.Component<IWizardStepProps> {
  static contextType = WizardContext;
  handleToggleRenewable = (event) => {
    const renewable = event.target.value;
    this.context.saveAnswers({
      renewable,
      greenElectricity: GREEN_ZERO_VALUE,
      greenGas: GREEN_ZERO_VALUE,
    });
  };

  handleGreenElec = (value) => {
    this.context.saveAnswers({
      greenElectricity: value,
    });
  };

  handleGreenGas = (value) => {
    this.context.saveAnswers({
      greenGas: value,
    });
  };

  render() {
    const { renewable, electricity, gas, greenElectricity, greenGas } = this.context.answers;
    const GREEN_DATA = getGreenData(false);
    return (
      <Container>
        <Content>
          <Heading>Do you want to support green energy from renewable sources?</Heading>
          <ContentText>
     
            For as little as 65c a week, you can reduce the environmental impact of
            your energy usage. We’ll offset your usage by putting more money into renewables,
            through the government-accredited GreenPower program or our own Green Gas program.&nbsp;
            <LinkText
              text={
                <>
                  Learn more
                  <span style={{ marginLeft: '4px' }}>
                    <LaunchIcon size={16} color="grey" />
                  </span>
                </>
              }
              href="https://www.originenergy.com.au/electricity-gas/green.html"
              target="_blank"
            />
          </ContentText>

          <CheckboxContainer>
            <Checkbox
              label="Yes please"
              name="renewable"
              type="radio"
              value={RADIO_TRUE}
              checked={renewable === RADIO_TRUE}
              onChange={this.handleToggleRenewable}
            >
              <YesIcon />
            </Checkbox>
            <Checkbox
              label="No thanks"
              name="renewable"
              type="radio"
              value={RADIO_FALSE}
              checked={renewable === RADIO_FALSE}
              onChange={this.handleToggleRenewable}
            >
              <NoIcon />
            </Checkbox>
          </CheckboxContainer>

          {renewable === RADIO_TRUE && (
            <>
              <Text fontWeight="medium" color="grey56" size="md">
                Awesome! Choose the amount you'd like to offset:
              </Text>

              <GreenOptionSelectors>
                {electricity && (
                  <SegmentedControlsContainer>
                    <Box marginBottom={2}>
                      <Text fontWeight="medium" color="grey56" size="md">
                        Electricity
                      </Text>
                    </Box>
                    <SegmentedControls
                      value={greenElectricity || GREEN_ZERO_VALUE}
                      onChange={this.handleGreenElec}
                      groupName="greenElectricity"
                      options={[
                        { label: '0%', value: '0.00' },
                        { label: '25%', value: '0.25' },
                        { label: '50%', value: '0.50' },
                        { label: '100%', value: '1.00' },
                      ]}
                    />
                    <Box marginTop={2}>
                      <Text data-id="green-elec-cost" size="xs">
                        {capitalize(
                          get(find(GREEN_DATA['01'], { value: greenElectricity }), 'greenCost'),
                        )}
                      </Text>
                    </Box>
                  </SegmentedControlsContainer>
                )}
                {gas && (
                  <SegmentedControlsContainer>
                    <Box marginBottom={2}>
                      <Text fontWeight="medium" color="grey56" size="md">
                        Natural gas
                      </Text>
                    </Box>
                    <SegmentedControls
                      value={greenGas || GREEN_ZERO_VALUE}
                      onChange={this.handleGreenGas}
                      groupName="greenGas"
                      options={[{ label: '0%', value: '0.00' }, { label: '100%', value: '1.00' }]}
                    />
                    <Box marginTop={2}>
                      <Text data-id="green-gas-cost" size="xs">
                        {capitalize(get(find(GREEN_DATA['02'], { value: greenGas }), 'greenCost'))}
                      </Text>
                    </Box>
                  </SegmentedControlsContainer>
                )}
              </GreenOptionSelectors>
            </>
          )}

          <Actions {...this.props} />
        </Content>
      </Container>
    );
  }
}
